import { useEffect, useRef, useState } from 'react';
import '../css/CopyLinkField.css';
import { copyTextToClipboard } from '../utils/clipboardUtil';
import {
  COPY_LINK_LABEL,
  COPY_LINK_COPIED_LABEL,
  COPY_LINK_FAILED_LABEL,
  COPY_LINK_FEEDBACK_MS,
} from '../constants/bookingConst';

const COPY_STATE = { IDLE: 'idle', COPIED: 'copied', FAILED: 'failed' };

const COPY_BUTTON_LABELS = {
  [COPY_STATE.IDLE]: COPY_LINK_LABEL,
  [COPY_STATE.COPIED]: COPY_LINK_COPIED_LABEL,
  [COPY_STATE.FAILED]: COPY_LINK_FAILED_LABEL,
};

/**
 * A link shown in a pill with a Copy button beside it.
 *
 * The link text is shown without its https:// prefix to fit one line, but
 * the full URL is what gets copied. The text is also press-and-hold
 * selectable, for webviews that block clipboard access.
 *
 * @param {object} props
 * @param {string} props.url - the absolute URL to show and copy
 * @returns {JSX.Element}
 */
function CopyLinkField({ url }) {
  const [copyState, setCopyState] = useState(COPY_STATE.IDLE);
  const resetTimerRef = useRef(null);

  // Cancels a pending label reset if the field goes away mid-feedback.
  useEffect(() => () => window.clearTimeout(resetTimerRef.current), []);

  /**
   * Copies the link and shows the outcome on the button for a moment.
   *
   * @returns {Promise<void>}
   */
  const handleCopy = async () => {
    const copied = await copyTextToClipboard(url);
    setCopyState(copied ? COPY_STATE.COPIED : COPY_STATE.FAILED);
    window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(
      () => setCopyState(COPY_STATE.IDLE),
      COPY_LINK_FEEDBACK_MS,
    );
  };

  return (
    <div className="clf">
      <span className="clf-url">{url.replace(/^https?:\/\//, '')}</span>
      <button
        type="button"
        className={`clf-copy clf-copy--${copyState}`}
        onClick={handleCopy}
        aria-live="polite"
      >
        {COPY_BUTTON_LABELS[copyState]}
      </button>
    </div>
  );
}

export default CopyLinkField;
